/**
 * Admin bulk-import API layer (P1 Tier-A, marketplace.bulk_import). Typed calls
 * to the SHIPPED EPIC-006 bulk-import router (/admin/import/*). The file upload
 * is a multipart POST done with a bespoke authenticated fetch (apiFetch always
 * JSON-encodes, so it can't carry FormData); the commit/cancel/status calls go
 * through apiClient (commit auto-attaches the Idempotency-Key). No backend
 * routes added. Shapes mirror bulk_import/schemas.py + the worker summaries.
 */
import { apiClient, getApiClientConfig } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";

export const IMPORT_PHASES = [
  "queued", "dry_run_running", "dry_run_completed", "committing", "completed", "failed",
] as const;
export type ImportPhase = (typeof IMPORT_PHASES)[number];

export const IMPORT_JOB_KINDS = ["sellers", "products", "sellers_and_products"] as const;
export type ImportJobKind = (typeof IMPORT_JOB_KINDS)[number];

export const IMPORT_ERROR_KINDS = [
  "required_field_missing", "phone_invalid_e164", "unknown_market", "unknown_category",
  "duplicate_within_file", "duplicate_against_db", "ssrf_blocked", "invalid_stock_state",
] as const;

export interface DryRunInlineError { row_index: number; kind: string; field: string; }
export interface DryRunSummary {
  valid_count: number;
  duplicate_count: number;
  error_count: number;
  error_count_by_kind: Record<string, number>;
  total_rows: number;
  inline_errors: DryRunInlineError[];
}
export interface CommitSummary {
  created_sellers: number;
  updated_sellers: number;
  created_products: number;
  updated_products: number;
  error_count: number;
  geocoded: number;
  image_ingestion_pending: number;
}
export interface ImportJobStatus {
  job_id: string;
  job_kind: string;
  phase: ImportPhase;
  filename: string | null;
  dry_run_summary: DryRunSummary | null;
  commit_summary: CommitSummary | null;
  failure_reason: string | null;
}
export interface ImportErrorsLink { url: string | null; }

export const importKeys = {
  job: (id: string) => ["admin", "import", "job", id] as const,
  errors: (id: string) => ["admin", "import", "errors", id] as const,
} as const;

/** Multipart upload → 202 {job_id}. `sellers_and_products` hits /products. */
export async function uploadImport(
  kind: ImportJobKind,
  file: File,
): Promise<{ job_id: string }> {
  const path = kind === "sellers" ? "/admin/import/sellers" : "/admin/import/products";
  const form = new FormData();
  form.append("file", file);
  const accessToken = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (accessToken !== null) headers["Authorization"] = "Bearer " + accessToken;
  const res = await fetch(getApiClientConfig().baseUrl + path, {
    method: "POST",
    body: form, // browser sets the multipart boundary; do NOT set Content-Type
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    let code = "import.upload_failed";
    try {
      const body = (await res.json()) as { code?: string; detail?: { code?: string } };
      code = body.detail?.code ?? body.code ?? code;
    } catch { /* non-JSON body */ }
    throw new Error(code);
  }
  return (await res.json()) as { job_id: string };
}

export function getImportJob(id: string): Promise<ImportJobStatus> {
  return apiClient.get<ImportJobStatus>(`/admin/import/jobs/${id}`);
}
export function getImportErrorsLink(id: string): Promise<ImportErrorsLink> {
  return apiClient.get<ImportErrorsLink>(`/admin/import/jobs/${id}/errors`);
}
export function commitImport(id: string): Promise<{ job_id: string }> {
  // Idempotency-Key auto-attached (commit is the replay-protected mutation).
  return apiClient.post<{ job_id: string }, Record<string, never>>(
    `/admin/import/jobs/${id}/commit`,
    {},
  );
}
export function cancelImport(id: string, reason: string): Promise<ImportJobStatus> {
  return apiClient.post<ImportJobStatus, { reason: string }>(
    `/admin/import/jobs/${id}/cancel`,
    { reason },
  );
}
